import { cn } from '@/lib/cn';
import { ChevronDown, MoreHorizontal } from 'lucide-react';
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const CloseMenuContext = createContext<(() => void) | null>(null);

type RowActionsDropdownProps = {
  label: string;
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  /** `labeled` mostra “Opções” + seta — mais claro para quem não conhece o ícone “…”. */
  trigger?: 'icon' | 'labeled';
  triggerText?: string;
};

export function RowActionsDropdown({
  label,
  children,
  onOpenChange,
  trigger = 'icon',
  triggerText = 'Opções',
}: RowActionsDropdownProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const menuWidth = menu?.offsetWidth ?? 224;
      const menuHeight = menu?.offsetHeight ?? 160;
      const gap = 4;

      let top = rect.bottom + gap;
      let left = rect.right - menuWidth;

      if (left < 8) left = 8;
      if (left + menuWidth > window.innerWidth - 8) {
        left = window.innerWidth - menuWidth - 8;
      }

      if (top + menuHeight > window.innerHeight - 8) {
        top = rect.top - menuHeight - gap;
      }

      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, children]);

  useEffect(() => {
    if (!open) return;

    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setMenuOpen(false);
    }

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  function setMenuOpen(next: boolean) {
    setOpen(next);
    onOpenChange?.(next);
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setMenuOpen(!open)}
        className={cn(
          'inline-flex items-center justify-center rounded-lg transition-colors',
          trigger === 'labeled'
            ? 'h-8 gap-1 px-2.5 text-sm font-medium'
            : 'h-8 w-8',
          open
            ? 'bg-manejai-cream text-manejai-forest'
            : 'text-manejai-bark/70 hover:bg-manejai-cream/70 hover:text-manejai-forest',
        )}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={label}
      >
        {trigger === 'labeled' ? (
          <>
            {triggerText}
            <ChevronDown
              className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
              aria-hidden
            />
          </>
        ) : (
          <MoreHorizontal className="h-4 w-4" />
        )}
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: position.top, left: position.left }}
            className="fixed z-[200] w-56 rounded-xl border border-manejai-sand bg-white shadow-elevated py-1"
          >
            <CloseMenuContext.Provider value={() => setMenuOpen(false)}>
              {children}
            </CloseMenuContext.Provider>
          </div>,
          document.body,
        )}
    </>
  );
}

export function RowActionsMenuItem({
  icon: Icon,
  label,
  onClick,
  danger = false,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  const close = useContext(CloseMenuContext);

  return (
    <button
      type="button"
      role="menuitem"
      onClick={() => {
        close?.();
        onClick();
      }}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors',
        danger
          ? 'text-red-600 hover:bg-red-50'
          : 'text-manejai-bark hover:bg-manejai-cream/50',
      )}
    >
      <Icon className={cn('h-4 w-4 shrink-0', danger ? 'text-red-500' : 'text-manejai-moss')} />
      {label}
    </button>
  );
}
